import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const Pension = () => {
  // State variables for Pension calculation
  const [averageSalary, setAverageSalary] = useState(0); // Average salary of the individual
  const [replacementRate, setReplacementRate] = useState(0); // Replacement rate for pension (as a percentage)
  const [serviceYears, setServiceYears] = useState(0); // Number of years worked
  const [pensionIncome, setPensionIncome] = useState(0); // Calculated pension income

  // Event handlers to update state values
  const handleAverageSalaryChange = (e) =>
    setAverageSalary(parseFloat(e.target.value) || 0);
  const handleReplacementRateChange = (e) =>
    setReplacementRate(parseFloat(e.target.value) || 0);
  const handleServiceYearsChange = (e) =>
    setServiceYears(parseInt(e.target.value) || 0);

  // useEffect hook to calculate pension income when any input changes
  useEffect(() => {
    if (averageSalary && replacementRate && serviceYears) {
      const calculatedPensionIncome =
        averageSalary * (replacementRate / 100) * serviceYears;
      setPensionIncome(calculatedPensionIncome);
    } else {
      setPensionIncome(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [averageSalary, replacementRate, serviceYears]);

  return (
    <Layout title="Pension Calculator" path="pension">
      <section className="section-padding" id="pension-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Pension Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Pension Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Average Salary Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="averageSalary" className="form-label">
                        Average Salary (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Average Salary:</strong> The average salary you earn throughout your career."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="averageSalary"
                        placeholder="Enter average salary"
                        value={averageSalary || ""}
                        onChange={handleAverageSalaryChange}
                      />
                    </div>

                    {/* Replacement Rate Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="replacementRate" className="form-label">
                        Replacement Rate (%)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Replacement Rate:</strong> The percentage of your final salary you wish to receive as pension."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="replacementRate"
                        placeholder="Enter replacement rate"
                        value={replacementRate || ""}
                        onChange={handleReplacementRateChange}
                      />
                    </div>

                    {/* Service Years Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="serviceYears" className="form-label">
                        Service Years
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Service Years:</strong> The total number of years you have worked."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="serviceYears"
                        placeholder="Enter number of service years"
                        value={serviceYears || ""}
                        onChange={handleServiceYearsChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Pension Income:{" "}
                        <span className="fw-bold ms-3">
                          {pensionIncome === 0
                            ? "0.00"
                            : pensionIncome.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                          ₹
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Pension Calculator
                </h3>

                {/* What is a Pension Calculator? */}
                <h4 className="mt-3 mb-3">What is a Pension Calculator?</h4>
                <p>
                  A **Pension Calculator** helps individuals estimate their
                  pension income based on their average salary, the replacement
                  rate, and their service years. It gives an idea of how much
                  pension one might expect upon retirement.
                </p>

                {/* Why Use a Pension Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a Pension Calculator?</h4>
                <p>
                  This calculator is essential for financial planning,
                  especially when preparing for retirement. It helps you
                  understand how much you can expect to receive monthly or
                  annually after retirement, based on your service and salary
                  history.
                </p>

                {/* Benefits of Using a Pension Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using a Pension Calculator
                </h4>
                <ul>
                  <li>
                    <strong>Helps in Financial Retirement Planning:</strong>{" "}
                    Estimates how much you can rely on pension income during
                    retirement.
                  </li>
                  <li>
                    <strong>
                      Supports Pension Planning for HR Departments:
                    </strong>{" "}
                    Useful for HR departments in organizations offering pension
                    schemes to employees.
                  </li>
                  <li>
                    <strong>Improves Financial Decision-Making:</strong>{" "}
                    Provides a clear view of future pension income, helping
                    individuals make informed financial decisions.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use a Pension Calculator?
                </h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Individuals Planning for Retirement:</strong> Helps
                    you understand your pension income based on your work
                    history and retirement planning.
                  </li>
                  <li>
                    <strong>HR and Financial Planners:</strong> Helps HR
                    departments and financial planners estimate and manage
                    pension benefits.
                  </li>
                  <li>
                    <strong>Employees of Pensioned Organizations:</strong>{" "}
                    Beneficial for employees working in companies with pension
                    schemes, providing insight into future retirement income.
                  </li>
                </ul>

                <h4 className="mt-4 mb-3">
                  What factors affect pension income?
                </h4>
                <p>
                  Factors such as your average salary, years of service, and the
                  replacement rate play a significant role in determining the
                  amount of pension income you will receive after retirement.
                </p>

                <h4 className="mt-4 mb-3">
                  Why is it important to estimate pension income early?
                </h4>
                <p>
                  Estimating your pension income early allows you to plan better
                  for your retirement. It helps you assess if your pension will
                  be sufficient to cover your retirement needs or if you need to
                  save more or invest in additional retirement funds.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Pension;
